import {
  Dispatch,
  RefObject,
  SetStateAction,
  useEffect,
} from 'react';
import { useSelector } from 'react-redux';

import type { Store } from 'app-redux/types/storeTypes';
import type { Block } from 'src/__generated__/graphqlTypes';

const THROTTLE_MS = 100;

export const useMobileStickyButtonView = (
  ctaButton: Block,
  setStickyCtaActivationState: Dispatch<SetStateAction<boolean>>,
  ctaRef: RefObject<HTMLAnchorElement>,
) => {
  /* Required as a dependency to update button header on navigation */
  const slug = useSelector((store: Store) => store.server.page.slug);

  useEffect(() => {
    const targetId = ctaButton?.title;

    if (!targetId) {
      /* By default offer button has to be just sticky */
      if (ctaButton) {
        setStickyCtaActivationState(true);
      }

      return;
    }

    /* On pages without CTA on a banner show header CTA from start */
    const targetEl = document.getElementById(targetId);

    if (!targetEl) {
      setStickyCtaActivationState(true);

      return;
    }

    let rafId: number | null = null;
    let timeoutId: number | null = null;
    let lastRunAt = 0;

    const requestRafUpdate = () => {
      if (rafId !== null) {
        return;
      }

      rafId = window.requestAnimationFrame(() => {
        rafId = null;

        let hasIntersectionWithTarget = false;
        const buttonEl = ctaRef.current;
        let targetEl: HTMLElement | null = null;

        if (targetId) {
          targetEl = document.getElementById(targetId);
        }

        if (buttonEl && targetEl) {
          const targetRect = targetEl.getBoundingClientRect();
          hasIntersectionWithTarget = targetRect.bottom < 0;
        }

        const nextVisible = hasIntersectionWithTarget;

        setStickyCtaActivationState((prev) => {
          if (nextVisible !== prev) {
            return nextVisible;
          }

          return prev;
        });
      });
    };

    /* Used for throttling */
    const scheduleUpdate = () => {
      const now = performance.now();
      const timeSinceLastRun = now - lastRunAt;

      if (timeSinceLastRun >= THROTTLE_MS) {
        lastRunAt = now;

        if (timeoutId !== null) {
          window.clearTimeout(timeoutId);
          timeoutId = null;
        }

        requestRafUpdate();

        return;
      }

      if (timeoutId === null) {
        timeoutId = window.setTimeout(() => {
          timeoutId = null;
          lastRunAt = performance.now();
          requestRafUpdate();
        }, THROTTLE_MS - timeSinceLastRun);
      }
    };

    requestRafUpdate();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });

    return () => {
      window.removeEventListener('scroll', scheduleUpdate);

      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }

      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [ctaButton?.title, setStickyCtaActivationState, slug]);
};
